/*
 * @file: 埋点去重管理器
 * @author: Spec
 */

interface ReportedExposureRecord {
    exposureKey: string;    // 生成的唯一键
    timestamp: number;
    count: number;          // 上报次数（用于调试和监控）
}

/**
 * 生成埋点唯一键
 * @param options 埋点参数
 * @returns 唯一键字符串
 */
export function generateExposureKey(options: {
    type: string;           // 埋点类型：view/clk/timing
    value: string;          // 埋点值
    customKey?: string;     // 可选自定义键
}): string {
    const baseKey = `${options.type}_${options.value}`;
    
    // 如果有自定义键，则加入作为唯一标识的一部分
    if (options.customKey) {
        return `${baseKey}_${options.customKey}`;
    }
    
    // 否则只使用type和value作为唯一键
    return baseKey;
}

/**
 * 埋点去重管理器
 * 负责管理所有展现埋点的上报状态，确保每个唯一埋点只上报一次
 */
class ExposureDeduplicationManager {
    private static instance: ExposureDeduplicationManager;
    private reportedExposures = new Map<string, ReportedExposureRecord>();
    private readonly maxAge = 24 * 60 * 60 * 1000; // 24小时过期时间

    private constructor() {
        // 私有构造函数，确保单例
    }

    /**
     * 获取去重管理器实例
     */
    static getInstance(): ExposureDeduplicationManager {
        if (!ExposureDeduplicationManager.instance) {
            ExposureDeduplicationManager.instance = new ExposureDeduplicationManager();
        }
        return ExposureDeduplicationManager.instance;
    }

    /**
     * 检查埋点是否已上报
     * @param exposureKey 埋点唯一键
     * @returns 是否已上报
     */
    hasReported(exposureKey: string): boolean {
        const record = this.reportedExposures.get(exposureKey);
        if (!record) {
            return false;
        }
        
        // 检查记录是否过期
        if (Date.now() - record.timestamp > this.maxAge) {
            this.reportedExposures.delete(exposureKey);
            return false;
        }
        
        return true;
    }

    /**
     * 标记埋点为已上报
     * @param exposureKey 埋点唯一键
     */
    markAsReported(exposureKey: string): void {
        const existing = this.reportedExposures.get(exposureKey);
        
        if (existing) {
            // 更新现有记录
            existing.count += 1;
            existing.timestamp = Date.now();
        } else {
            // 创建新记录
            this.reportedExposures.set(exposureKey, {
                exposureKey,
                timestamp: Date.now(),
                count: 1
            });
        }

        // 开发环境下输出调试信息
        if (process.env.NODE_ENV !== 'production') {
            console.log(`埋点上报记录: ${exposureKey}, 上报次数: ${this.reportedExposures.get(exposureKey)?.count}`);
        }
    }

    /**
     * 清理过期记录
     */
    cleanupExpiredRecords(): void {
        const now = Date.now();
        let cleanedCount = 0;
        
        for (const [key, record] of this.reportedExposures.entries()) {
            if (now - record.timestamp > this.maxAge) {
                this.reportedExposures.delete(key);
                cleanedCount++;
            }
        }

        if (process.env.NODE_ENV !== 'production' && cleanedCount > 0) {
            console.log(`清理了 ${cleanedCount} 条过期埋点记录`);
        }
    }

    /**
     * 强制重新上报某个埋点（调试用）
     * @param exposureKey 埋点唯一键
     */
    forceReReport(exposureKey: string): void {
        this.reportedExposures.delete(exposureKey);
        if (process.env.NODE_ENV !== 'production') {
            console.log(` 强制重新上报埋点: ${exposureKey}`);
        }
    }

    /**
     * 获取统计数据（调试用）
     */
    getStats(): { total: number; records: ReportedExposureRecord[] } {
        const records = Array.from(this.reportedExposures.values());
        return {
            total: records.length,
            records: records.sort((a, b) => b.timestamp - a.timestamp)
        };
    }
}

// 导出单例实例
export const deduplicationManager = ExposureDeduplicationManager.getInstance();